import { useState, useMemo, useEffect } from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  Control,
  FieldValues,
} from "react-hook-form";
import {
  Plus,
  Trash2,
  Wrench,
  ChevronDown,
  ChevronRight,
  Lock,
} from "lucide-react";
import Button from "../UI/Button";
import { Select, Input } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import { CreateShipmentData } from "../../types";
import { Service } from "../../types/service";
import { useTranslation } from "react-i18next";
import { ServiceAddInline } from "./ServiceAddInline";
import { listHasTax, sumTax, taxCellLabel } from "../../utils/tax";

interface MatrixRowState {
  selectedZoneId: string | null;
  selectedBandId: string | null;
  frozenZoneName: string | null;
  frozenBandLabel: string | null;
  frozenPrice: number | null;
  isHistoric: boolean;
}

/**
 * Shared by the shipment form and the container order form. Both carry an
 * identically-shaped `services` array, so the section is generic over the form
 * type and casts to the shipment shape internally for the field paths.
 */
interface ServicesSectionProps<T extends FieldValues = CreateShipmentData> {
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  readonly: boolean;
  serviceFields: any[];
  appendService: (item: any) => void;
  removeService: (index: number) => void;
  totalForEachRow: any[];
  grandTotal: number;
  services: Service[];
  setValue: UseFormSetValue<T>;
  control: Control<T>;
  /**
   * Post-completion mode: the shipment is Completed / locked so the form can't
   * be unlocked, but services may still be managed from the system side via
   * dedicated API endpoints. Add + delete of uninvoiced rows only.
   */
  /**
   * What this block is called, when the caller wants something else.
   *
   * Defaults to the shipment wording ("Additional Services"), because that is
   * what this component was built for and what every shipment screen still says.
   * The container order module calls the same rows "Invoicing Services" — same
   * table, same behaviour, different name in that context — and overriding the
   * title here is what lets one component serve both without a second copy or a
   * rename that would reach screens nobody asked to change.
   */
  title?: string;
  postCompletionMode?: boolean;
  onApiAddService?: (service: Record<string, unknown>) => Promise<void>;
  onApiDeleteService?: (serviceId: string) => Promise<void>;
  /**
   * Hide the per-row "Invoiced" column. Container order services are templates
   * copied onto generated shipments — invoicing happens on those children, so
   * an order row has no invoice state to show.
   */
  hideInvoicedColumn?: boolean;
  /**
   * Hide the per-row "By" column.
   *
   * The column exists to separate a charge the office booked from one the
   * driver added from the road, and exactly one flow in the product writes the
   * latter: `PATCH /driver/shipments/:id/services`, which stamps
   * `addedBy: "driver"` onto a *shipment's* services. Containers have no
   * equivalent — a driver executing a container may record steps, weights, the
   * seal and documents, and nothing else — so every row on a container is a
   * green "SU" and the column is a fifteenth of the table spent saying so.
   *
   * Off by default, so shipments keep it without asking.
   */
  hideByColumn?: boolean;
  /**
   * Rows that are locked by something other than their own invoice, keyed by
   * row `_id`, with the reason to show on the lock.
   *
   * A shipment's charge is locked by its own `invoiceId` and nothing else, and
   * that stays the rule here. A *container order's* charge is different: it is
   * the batch's price list, and it is frozen once any container generated from
   * it has been invoiced for that charge — a fact that lives on the containers,
   * not on the row being drawn. The server sends which rows those are; this is
   * where the table is told, so the row is drawn locked instead of letting
   * someone retype a price the save is going to refuse.
   *
   * Empty for every shipment screen, which is why it is optional.
   */
  lockedRows?: Record<string, string>;
}

/**
 * Whether the catalogue still offers the card this row was priced from.
 *
 * By id when the row records one. The old answer — "is there any service with
 * this name?" — was wrong in both directions once a code could name two cards:
 * delete the client's own rate and the row still matched the general card, so a
 * row whose rate had gone read as healthy; rename the general card and a
 * perfectly billable row read as deleted.
 *
 * Rows stored before the picker recorded a card have nothing to match but the
 * name, which is the behaviour they have always had.
 */
function getRowServiceStatus(
  serviceName: string,
  services: Service[],
  catalogServiceId?: string | null,
): "found" | "deleted" | "empty" {
  if (!serviceName) return "empty";
  if (catalogServiceId) {
    return services.some((s) => s._id === catalogServiceId)
      ? "found"
      : "deleted";
  }
  return services.some((s) => s.name === serviceName) ? "found" : "deleted";
}

export function ServicesSection<T extends FieldValues = CreateShipmentData>({
  register: registerProp,
  readonly,
  serviceFields,
  appendService,
  removeService,
  totalForEachRow,
  services,
  setValue: setValueProp,
  control: controlProp,
  grandTotal,
  postCompletionMode = false,
  onApiAddService,
  onApiDeleteService,
  hideInvoicedColumn = false,
  hideByColumn = false,
  lockedRows,
  title,
}: ServicesSectionProps<T>) {
  const register = registerProp as unknown as UseFormRegister<CreateShipmentData>;
  const setValue = setValueProp as unknown as UseFormSetValue<CreateShipmentData>;
  const control = controlProp as unknown as Control<CreateShipmentData>;

  const { t } = useTranslation("shipments");
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [partnerFilter, setPartnerFilter] = useState<"GENERAL" | string>(
    "GENERAL",
  );
  const [matrixRows, setMatrixRows] = useState<Record<number, MatrixRowState>>(
    {},
  );
  // Post-completion inline add row (API-backed; no form state involved).
  const [showApiAdd, setShowApiAdd] = useState(false);
  const [apiBusy, setApiBusy] = useState(false);

  // In post-completion mode services are managed through dedicated endpoints
  // even though the rest of the form stays readonly.
  const canManage = postCompletionMode && !!onApiAddService;
  const showActions = !readonly || canManage;

  // The Tax column is always shown (rows without a VAT category render "—").
  // The tax totals in the footer only appear when at least one row carries a
  // VAT category (i.e. anything other than NO_TAX).
  const hasAnyTax = listHasTax(totalForEachRow);
  const grandTax = sumTax(totalForEachRow);

  const availablePartners = useMemo(() => {
    const seen = new Map<string, string>();
    services.forEach((s) => {
      if (
        s.partnerId &&
        s.partnerInfo?.name &&
        !seen.has(s.partnerId) &&
        !s.partnerInfo.isDeleted
      ) {
        seen.set(s.partnerId, s.partnerInfo.name);
      }
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [services]);

  const visibleServices = useMemo(() => {
    if (partnerFilter === "GENERAL") {
      return services.filter((s) => s.partnerId == null);
    }
    return services.filter((s) => s.partnerId === partnerFilter);
  }, [services, partnerFilter]);

  useEffect(() => {
    serviceFields.forEach((_, index) => {
      const rowValues = control._formValues?.services?.[index];
      if (!rowValues) return;
      if (rowValues.type !== "MATRIX") return;
      if (matrixRows[index]) return;
      const mc = rowValues.matrixContext;
      if (!mc) return;

      const svc = services.find((s) => s.name === rowValues.name);
      const zoneStillExists =
        svc?.zones?.some((z) => z._id === mc.selectedZoneId) ?? false;
      const bandStillExists =
        svc?.bands?.some((b) => b._id === mc.selectedBandId) ?? false;

      setMatrixRows((prev) => ({
        ...prev,
        [index]: {
          selectedZoneId: zoneStillExists ? mc.selectedZoneId : null,
          selectedBandId: bandStillExists ? mc.selectedBandId : null,
          frozenZoneName: mc.zoneName ?? null,
          frozenBandLabel: mc.bandLabel ?? null,
          frozenPrice: rowValues.price ?? null,
          isHistoric: true,
        },
      }));
    });
  }, [serviceFields.length]);

  const addService = () => {
    appendService({
      // Set when a card is picked — see handleServiceSelect.
      catalogServiceId: null,
      name: "",
      type: "HOUR",
      price: 0,
      qty: 1,
      total: 0,
      description: "",
      tax: "NO_TAX",
      partnerId: null,
      matrixContext: null,
    });
    setIsCollapsed(false);
  };

  /**
   * A row is picked by CARD, not by code.
   *
   * The dropdown used to be keyed on the service name, and a company may price
   * one code twice — once generally, once for a particular client. Two options
   * with the same value are the same option to a browser, so whichever card
   * came first in the loaded list won every click, and the row recorded a rate
   * nobody had chosen. Keying on the id makes the two distinguishable, and
   * `catalogServiceId` is what carries the choice through to invoicing.
   */
  const handleServiceSelect = (index: number, selectedId: string) => {
    const selectedService = services.find((s) => s._id === selectedId);
    if (!selectedService) return;

    setValue(`services.${index}.catalogServiceId` as any, selectedService._id);
    setValue(`services.${index}.name`, selectedService.name);
    setValue(`services.${index}.type`, selectedService.type);
    setValue(
      `services.${index}.description`,
      selectedService.description ?? "",
    );
    // Inherit the VAT category from the catalogue entry, for invoicing.
    setValue(`services.${index}.tax`, selectedService.tax ?? "NO_TAX");
    setValue(`services.${index}.partnerId`, selectedService.partnerId ?? null);

    if (selectedService.type === "MATRIX") {
      setValue(`services.${index}.price`, 0);
      setValue(`services.${index}.total`, 0);
      setValue(`services.${index}.matrixContext`, undefined);

      setMatrixRows((prev) => ({
        ...prev,
        [index]: {
          selectedZoneId: null,
          selectedBandId: null,
          frozenZoneName: null,
          frozenBandLabel: null,
          frozenPrice: null,
          isHistoric: false,
        },
      }));
    } else {
      setValue(`services.${index}.price`, selectedService.price ?? 0);
      const qty = control._formValues?.services?.[index]?.qty || 1;
      setValue(`services.${index}.total`, (selectedService.price ?? 0) * qty);
      setValue(`services.${index}.matrixContext`, undefined);

      setMatrixRows((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  const resolveMatrixPrice = (
    index: number,
    zoneId: string | null,
    bandId: string | null,
    svc: Service,
  ) => {
    if (!zoneId || !bandId) {
      setValue(`services.${index}.price`, 0);
      setValue(`services.${index}.total`, 0);
      setValue(`services.${index}.matrixContext`, undefined);
      return;
    }

    const cell = svc.priceMatrix?.find(
      (c) => c.zoneId === zoneId && c.bandId === bandId,
    );

    if (!cell) {
      setValue(`services.${index}.price`, 0);
      setValue(`services.${index}.total`, 0);
      setValue(`services.${index}.matrixContext`, undefined);
      return;
    }

    const zone = svc.zones?.find((z) => z._id === zoneId);
    const band = svc.bands?.find((b) => b._id === bandId);
    const qty = control._formValues?.services?.[index]?.qty || 1;

    setValue(`services.${index}.price`, cell.price);
    setValue(`services.${index}.total`, cell.price * qty);
    setValue(`services.${index}.matrixContext`, {
      zoneName: zone?.name ?? "",
      bandLabel: band?.label ?? "",
      selectedZoneId: zoneId,
      selectedBandId: bandId,
    });
  };

  const handleZoneChange = (index: number, zoneId: string) => {
    const serviceName = control._formValues?.services?.[index]?.name;
    const svc = services.find((s) => s.name === serviceName);
    if (!svc) return;
    const newBandId = matrixRows[index]?.selectedBandId ?? null;
    setMatrixRows((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        selectedZoneId: zoneId || null,
        isHistoric: false,
      },
    }));
    resolveMatrixPrice(index, zoneId || null, newBandId, svc);
  };

  const handleBandChange = (index: number, bandId: string) => {
    const serviceName = control._formValues?.services?.[index]?.name;
    const svc = services.find((s) => s.name === serviceName);
    if (!svc) return;
    const newZoneId = matrixRows[index]?.selectedZoneId ?? null;
    setMatrixRows((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        selectedBandId: bandId || null,
        isHistoric: false,
      },
    }));
    resolveMatrixPrice(index, newZoneId, bandId || null, svc);
  };

  const handleRemoveService = (index: number) => {
    removeService(index);
    setMatrixRows((prev) => {
      const next: Record<number, MatrixRowState> = {};
      Object.entries(prev).forEach(([k, v]) => {
        const ki = parseInt(k, 10);
        if (ki < index) next[ki] = v;
        else if (ki > index) next[ki - 1] = v;
      });
      return next;
    });
  };

  // ── Post-completion (API-backed) service management ──────────────────────
  const handleApiDelete = async (serviceId: string) => {
    if (!onApiDeleteService) return;
    if (!window.confirm(t("services.confirmDelete"))) return;
    setApiBusy(true);
    try {
      await onApiDeleteService(serviceId);
    } finally {
      setApiBusy(false);
    }
  };

  const handleApiSave = async (payload: Record<string, unknown>) => {
    if (!onApiAddService) return;
    setApiBusy(true);
    try {
      await onApiAddService(payload);
      setShowApiAdd(false);
    } finally {
      setApiBusy(false);
    }
  };

  // Cells wrap rather than run on. The table is laid out fixed (see below), so
  // a long description takes a second line inside its column instead of pushing
  // the table past the pane and putting a horizontal scrollbar under it.
  // Text cells wrap inside their column instead of widening the table.
  const td = "px-2 py-1.5 align-top wrap-break-word";
  // Cells whose content is a single indivisible token — a price, a quantity,
  // a status badge. `wrap-break-word` would split "€130.00" across two lines
  // rather than let it touch the column edge, which is worse than either.
  // The column shares below are sized so these never need to.
  const tdTight = "px-2 py-1.5 align-top whitespace-nowrap";
  const th =
    "px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider";

  /**
   * A saved value in read mode, presented rather than shown as a greyed-out
   * form. A disabled input still costs the reader a box, a border and a
   * placeholder per value, and a `<select>` that cannot be opened is worse: it
   * offers a control and then refuses it. Read mode prints the value.
   */
  const ReadCell = ({ value }: { value?: React.ReactNode }) => {
    const empty =
      value === undefined || value === null || value === "" || value === "—";
    return (
      <span className={`text-[12px] ${empty ? "text-gray-300" : "text-ink"}`}>
        {empty ? "—" : value}
      </span>
    );
  };

  const renderZoneCell = (index: number, locked: boolean = readonly) => {
    const rowValues = control._formValues?.services?.[index];
    if (!rowValues || rowValues.type !== "MATRIX") {
      return <td className={`${td} text-gray-300`}>—</td>;
    }

    const serviceName = rowValues.name;
    const svc = services.find((s) => s.name === serviceName);
    const rowState = matrixRows[index];

    if (!svc) {
      const frozenName =
        rowState?.frozenZoneName ?? rowValues.matrixContext?.zoneName;
      return (
        <td className={td}>
          <div className="flex items-center gap-1">
            <span className="text-[12px] text-gray-600">{frozenName || "—"}</span>
            <span
              className="text-[10px] bg-amber-100 text-amber-700 px-1 rounded"
              title="Service no longer available"
            >
              {t("services.historic")}
            </span>
          </div>
        </td>
      );
    }

    const savedZoneId =
      rowState?.selectedZoneId ?? rowValues.matrixContext?.selectedZoneId;
    const savedZoneStillExists =
      savedZoneId && svc.zones?.some((z) => z._id === savedZoneId);
    const hasMismatch =
      rowState?.isHistoric && savedZoneId && !savedZoneStillExists;

    if (hasMismatch) {
      const frozenName =
        rowState?.frozenZoneName ?? rowValues.matrixContext?.zoneName;
      return (
        <td className={td}>
          <div className="flex flex-col gap-0.5">
            <span className="text-[12px] text-amber-600 line-through">
              {frozenName}
            </span>
            <span className="text-[10px] text-red-500">
              {t("services.zoneRemoved")}
            </span>
            {/* The re-pick, offered only where there is something to pick
                with. Read mode says what happened and stops there. */}
            {!readonly && (
              <select
                value=""
                onChange={(e) => handleZoneChange(index, e.target.value)}
                disabled={locked}
                className="w-full h-8 px-2.5 py-1 text-[13px] border border-red-400 rounded-md mt-0.5"
              >
                <option value="">{t("services.selectZone")}</option>
                {(svc.zones ?? []).map((z) => (
                  <option key={z._id} value={z._id}>
                    {z.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </td>
      );
    }

    const currentZoneId = rowState?.selectedZoneId ?? null;
    const isRequired = !currentZoneId;

    if (readonly) {
      // The zone as booked. The picker's own value first, the frozen name off
      // the row second — a row read back from the record has the latter and
      // may never have had the former in this session.
      const zoneName =
        svc.zones?.find((z) => z._id === currentZoneId)?.name ??
        rowValues.matrixContext?.zoneName;
      return (
        <td className={td}>
          <ReadCell value={zoneName} />
        </td>
      );
    }

    return (
      <td className={td}>
        <select
          value={currentZoneId ?? ""}
          onChange={(e) => handleZoneChange(index, e.target.value)}
          disabled={locked}
          className={`w-full h-8 px-2.5 py-1 text-[13px] border rounded-md ${
            isRequired ? "border-red-400 " : "border-gray-300 "
          }`}
        >
          <option value="">{t("services.selectZone")}</option>
          {(svc.zones ?? []).map((z) => (
            <option key={z._id} value={z._id}>
              {z.name}
            </option>
          ))}
        </select>
      </td>
    );
  };

  const renderBandCell = (index: number, locked: boolean = readonly) => {
    const rowValues = control._formValues?.services?.[index];
    if (!rowValues || rowValues.type !== "MATRIX") {
      return <td className={`${td} text-gray-300`}>—</td>;
    }

    const serviceName = rowValues.name;
    const svc = services.find((s) => s.name === serviceName);
    const rowState = matrixRows[index];

    if (!svc) {
      const frozenLabel =
        rowState?.frozenBandLabel ?? rowValues.matrixContext?.bandLabel;
      return (
        <td className={td}>
          <div className="flex items-center gap-1">
            <span className="text-[12px] text-gray-600">
              {frozenLabel || "—"}
            </span>
            <span className="text-[10px] bg-amber-100 text-amber-700 px-1 rounded">
              {t("services.historic")}
            </span>
          </div>
        </td>
      );
    }

    const savedBandId =
      rowState?.selectedBandId ?? rowValues.matrixContext?.selectedBandId;
    const savedBandStillExists =
      savedBandId && svc.bands?.some((b) => b._id === savedBandId);
    const hasMismatch =
      rowState?.isHistoric && savedBandId && !savedBandStillExists;

    if (hasMismatch) {
      const frozenLabel =
        rowState?.frozenBandLabel ?? rowValues.matrixContext?.bandLabel;
      return (
        <td className={td}>
          <div className="flex flex-col gap-0.5">
            <span className="text-[12px] text-amber-600 line-through">
              {frozenLabel}
            </span>
            <span className="text-[10px] text-red-500">
              {t("services.bandRemoved")}
            </span>
            {!readonly && (
              <select
                value=""
                onChange={(e) => handleBandChange(index, e.target.value)}
                disabled={locked}
                className="w-full h-8 px-2.5 py-1 text-[13px] border border-red-400 rounded-md mt-0.5"
              >
                <option value="">{t("services.selectBand")}</option>
                {(svc.bands ?? []).map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </td>
      );
    }

    const currentBandId = rowState?.selectedBandId ?? null;
    const isRequired = !currentBandId;

    if (readonly) {
      const bandLabel =
        svc.bands?.find((b) => b._id === currentBandId)?.label ??
        rowValues.matrixContext?.bandLabel;
      return (
        <td className={td}>
          <ReadCell value={bandLabel} />
        </td>
      );
    }

    return (
      <td className={td}>
        <select
          value={currentBandId ?? ""}
          onChange={(e) => handleBandChange(index, e.target.value)}
          disabled={locked}
          className={`w-full h-8 px-2.5 py-1 text-[13px] border rounded-md ${
            isRequired ? "border-red-400 " : "border-gray-300 "
          }`}
        >
          <option value="">{t("services.selectBand")}</option>
          {(svc.bands ?? []).map((b) => (
            <option key={b._id} value={b._id}>
              {b.label}
            </option>
          ))}
        </select>
      </td>
    );
  };

  return (
    <SectionCard
      icon={Wrench}
      title={title ?? t("services.title")}
      collapsible
      onToggle={() => setIsCollapsed((p) => !p)}
      actions={
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
          {(!readonly || canManage) && !isCollapsed && (
            <>
              <select
                value={partnerFilter}
                onChange={(e) => setPartnerFilter(e.target.value)}
                className="h-8 max-w-45 min-w-0 rounded-md border border-gray-300 bg-white px-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="GENERAL">{t("services.generalFilter")}</option>
                {availablePartners.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                onClick={() => {
                  if (canManage) {
                    setShowApiAdd(true);
                    setIsCollapsed(false);
                  } else {
                    addService();
                  }
                }}
                variant="primary"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                {t("services.addService")}
              </Button>
            </>
          )}
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      }
    >
      {isCollapsed ? (
        serviceFields.length > 0 ? (
          <p className="text-[12px] text-gray-500">
            {serviceFields.length}{" "}
            {serviceFields.length === 1
              ? t("services.serviceCount_one", { defaultValue: "service" })
              : t("services.serviceCount_other", { defaultValue: "services" })}{" "}
            · € {grandTotal}
          </p>
        ) : null
      ) : (
        <>
        {serviceFields.length > 0 && (
        <div className="border border-gray-200 rounded-md overflow-hidden">
          {/* Wide enough to read, never wider than it has to be.

              `table-fixed` with the columns in percentages makes the table a
              share-out of whatever width it is given rather than the sum of
              twelve minimum widths — which is what used to force a horizontal
              scrollbar under every charges table, with the totals a drag away
              from the rows they total.

              `min-w-[960px]` is the floor below which the columns stop being
              readable — a price wrapping mid-number, a badge cut in half. Above
              it (a laptop and up, in any pane this table appears in) `w-full`
              wins and the table simply fits, no scrollbar. Below it the wrapper
              scrolls, which on a phone is the honest answer: twelve columns of
              charges do not fit in 400px and squeezing them there helps nobody.

              The percentages are shares, not sizes, so a hidden column's share
              is redistributed and the same numbers serve every combination of
              the two optional columns. */}
          <div className="overflow-x-auto">
            <table
              className={`w-full table-fixed ${
                // One column fewer needs one column less of floor.
                hideByColumn ? "min-w-[920px]" : "min-w-[960px]"
              }`}
            >
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {!hideByColumn && (
                    <th className={`${th} w-[4%]`}>{t("services.by")}</th>
                  )}
                  {/* A code, not a sentence — "SRV-001" and the picker that
                      chooses it both sit inside 10%, and the description is
                      the column that actually needed the other three. */}
                  <th className={`${th} w-[10%]`}>
                    {t("services.serviceCode")}
                  </th>
                  <th className={`${th} w-[16%]`}>
                    {t("services.description")}
                  </th>
                  <th className={`${th} w-[7%]`}>
                    {t("services.unitType")}
                  </th>
                  <th className={`${th} w-[9%]`}>
                    {t("services.zones")}
                  </th>
                  <th className={`${th} w-[9%]`}>
                    {t("services.bands")}
                  </th>
                  <th className={`${th} w-[8%]`}>{t("services.price")}</th>
                  <th className={`${th} w-[5%]`}>{t("services.qty")}</th>
                  <th className={`${th} w-[10%]`}>{t("services.tax")}</th>
                  <th className={`${th} w-[8%]`}>
                    {t("services.totalExcludingTax")}
                  </th>
                  {!hideInvoicedColumn && (
                    <th className={`${th} w-[9%]`}>
                      {t("services.invoicedColumn")}
                    </th>
                  )}
                  {showActions && (
                    <th className={`${th} w-[5%]`}>
                      {t("services.action")}
                    </th>
                  )}
                </tr>
              </thead>

              <tbody className="bg-white">
                {serviceFields.map((field, index) => {
                  // Only the "By" badge reads this, so it is only meaningful
                  // where that column is shown.
                  const isDriver = field.addedBy?.toLowerCase() === "driver";
                  const rowValues = control._formValues?.services?.[index];
                  const isMatrix = rowValues?.type === "MATRIX";
                  const serviceStatus = getRowServiceStatus(
                    rowValues?.name ?? "",
                    services,
                    rowValues?.catalogServiceId,
                  );
                  // A row already on a locked invoice is immutable: inputs
                  // stay disabled even in edit mode and it can't be deleted.
                  const rowInvoiced = !!(
                    rowValues?.invoiceId ?? field.invoiceId
                  );
                  const rowServiceId: string | undefined =
                    rowValues?._id ?? field._id;
                  // ...and so is one the caller has frozen for a reason of its
                  // own — for a container order, that the batch has been billed
                  // for this charge. Same lock, different sentence on it.
                  const lockedReason = rowServiceId
                    ? lockedRows?.[rowServiceId]
                    : undefined;
                  const rowFrozen = rowInvoiced || !!lockedReason;
                  const rowLocked = readonly || rowFrozen;
                  // Net (price × qty), the row's tax category, and the gross
                  // total (net + tax). Total shows gross when tax applies.
                  const rowNet =
                    totalForEachRow[index]?.total ??
                    (field.price || 0) * (field.qty || 0);
                  const rowTax = totalForEachRow[index]?.tax ?? field.tax;

                  return (
                    <tr
                      key={field.id || index}
                      className="border-b border-gray-100 last:border-0"
                    >
                      {!hideByColumn && (
                        <td className={td}>
                          <div
                            className={`inline-flex w-5 h-5 rounded-full items-center justify-center text-[10px] font-semibold ${
                              isDriver
                                ? "bg-blue-600 text-white"
                                : "bg-green-600 text-white"
                            }`}
                          >
                            {isDriver
                              ? t("services.driverBadge")
                              : t("services.systemUser")}
                          </div>
                        </td>
                      )}

                      <td className={td}>
                        {readonly ? (
                          <div>
                            <ReadCell value={rowValues?.name?.trim()} />
                            {serviceStatus === "deleted" &&
                              rowValues?.name?.trim() && (
                                <span className="text-[10px] text-amber-600 mt-0.5 block">
                                  {t("services.serviceDeleted")}
                                </span>
                              )}
                          </div>
                        ) : (() => {
                          const selectedName = rowValues?.name?.trim();
                          const selectedId = rowValues?.catalogServiceId
                            ? String(rowValues.catalogServiceId)
                            : "";

                          /**
                           * The card this row is sitting on, if it is still on
                           * offer. A row keeps its own entry in the list even
                           * when the card has been retired or belongs to the
                           * other tab, so opening an old row never silently
                           * clears the code it was priced at.
                           */
                          const current =
                            (selectedId &&
                              services.find((s) => s._id === selectedId)) ||
                            null;
                          const inList = current
                            ? visibleServices.some((s) => s._id === current._id)
                            : false;

                          /**
                           * Two cards can share a code — the general rate and
                           * a client's own — so the label has to say which is
                           * which. Keying the option on the id already makes
                           * them two different options to the browser; naming
                           * the client makes them two different options to the
                           * person choosing.
                           */
                          const optionLabel = (svc: Service) =>
                            svc.partnerId
                              ? `${svc.name} · ${svc.partnerInfo?.name ?? t("services.clientRate")}`
                              : svc.name;

                          const options = [
                            ...visibleServices.map((s) => ({
                              label: optionLabel(s),
                              value: s._id,
                            })),
                          ];
                          if (current && !inList) {
                            options.push({
                              label: optionLabel(current),
                              value: current._id,
                            });
                          }
                          // A row priced before the picker recorded a card, or
                          // one whose card is gone: it still has to show what it
                          // says, so it gets an entry keyed on its own name.
                          const legacyValue = `name:${selectedName ?? ""}`;
                          if (!current && selectedName) {
                            options.push({
                              label: selectedName,
                              value: legacyValue,
                            });
                          }

                          return (
                            <div>
                              <Select
                                options={options}
                                disabled={rowLocked}
                                size="sm"
                                placeholder={t("services.selectService")}
                                value={
                                  current
                                    ? current._id
                                    : selectedName
                                      ? legacyValue
                                      : ""
                                }
                                onChange={(e) =>
                                  handleServiceSelect(index, e.target.value)
                                }
                                // No max-width: the column is a share of the
                                // card now, and a select capped at 220px inside
                                // a 14% column just leaves a gap.
                                className="w-full"
                                style={{ minWidth: 0 }}
                              />
                              {serviceStatus === "deleted" && selectedName && (
                                <span className="text-[10px] text-amber-600 mt-0.5 block">
                                  {t("services.serviceDeleted")}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>

                      <td className={td}>
                        {readonly ? (
                          <ReadCell value={rowValues?.description} />
                        ) : (
                          <Input
                            {...register(
                              `services.${index}.description` as const,
                            )}
                            type="text"
                            readOnly={rowLocked}
                            size="sm"
                            placeholder={t("services.enterDescription")}
                          />
                        )}
                      </td>

                      <td className={`${tdTight} text-[12px]`}>
                        {rowValues?.type || "–"}
                      </td>

                      {renderZoneCell(index, rowLocked)}
                      {renderBandCell(index, rowLocked)}

                      <td className={tdTight}>
                        {isMatrix || readonly ? (
                          <span className="text-[12px] font-medium text-gray-700">
                            {rowValues?.price
                              ? `€${Number(rowValues.price).toFixed(2)}`
                              : "—"}
                          </span>
                        ) : (
                          <Input
                            type="text"
                            inputMode="decimal"
                            placeholder="0.00"
                            size="sm"
                            {...register(`services.${index}.price` as const, {
                              validate: (value) => {
                                if (value === undefined)
                                  return t("services.priceRequired");
                                const regex = /^\d{1,5}(\.\d{1,2})?$/;
                                return regex.test(value.toString())
                                  ? true
                                  : t("services.priceFormat");
                              },
                              onChange: (e) => {
                                let input = e.target.value;
                                if (!/^\d{0,5}(\.\d{0,2})?$/.test(input)) {
                                  input = input.slice(0, -1);
                                }
                                const price = parseFloat(input) || 0;
                                const qty =
                                  control._formValues?.services?.[index]?.qty ||
                                  1;
                                setValue(`services.${index}.price`, input);
                                setValue(
                                  `services.${index}.total`,
                                  price * qty,
                                );
                              },
                            })}
                            disabled={rowLocked}
                            className="text-right"
                          />
                        )}
                      </td>

                      <td className={tdTight}>
                        {readonly ? (
                          <ReadCell value={rowValues?.qty} />
                        ) : (
                        <Input
                          type="text"
                          inputMode="numeric"
                          placeholder="0"
                          size="sm"
                          {...register(`services.${index}.qty` as const, {
                            validate: (value) => {
                              if (value === undefined)
                                return t("services.qtyRequired");
                              const regex = /^\d{1,5}$/;
                              return regex.test(value.toString())
                                ? true
                                : t("services.qtyFormat");
                            },
                            onChange: (e) => {
                              let input = e.target.value;
                              if (input === "") {
                                setValue(`services.${index}.qty`, 1);
                                setValue(`services.${index}.total`, 0);
                                return;
                              }
                              if (!/^\d{0,5}$/.test(input)) {
                                input = input.slice(0, -1);
                              }
                              const qty = parseInt(input) || 0;
                              const price = parseFloat(
                                control._formValues?.services?.[index]?.price ||
                                  0,
                              );
                              setValue(`services.${index}.qty`, input);
                              setValue(
                                `services.${index}.total`,
                                price * qty,
                              );
                              if (isMatrix) {
                                const mc =
                                  control._formValues?.services?.[index]
                                    ?.matrixContext;
                                if (mc && price > 0) {
                                  setValue(
                                    `services.${index}.total`,
                                    price * qty,
                                  );
                                }
                              }
                            },
                          })}
                          disabled={rowLocked}
                          className="text-right"
                        />
                        )}
                      </td>

                      <td className={`${td} text-[12px] text-gray-600`}>
                        {taxCellLabel(rowNet, rowTax)}
                      </td>

                      <td className={`${tdTight} text-[12px] font-medium`}>
                        €{rowNet.toFixed(2)}
                      </td>

                      {!hideInvoicedColumn && (
                        <td className={tdTight}>
                          {rowInvoiced ? (
                            <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">
                              {t("services.invoicedBadge")}
                            </span>
                          ) : (
                            <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium">
                              {t("services.uninvoicedBadge")}
                            </span>
                          )}
                        </td>
                      )}

                      {showActions && (
                        <td className={td}>
                          {rowFrozen ? (
                            // Frozen rows are immutable — locked, no action.
                            // The tooltip is the caller's when it has one, so a
                            // container order can say which containers froze it
                            // rather than making the user go and find out.
                            <span
                              className="inline-flex items-center justify-center p-1 text-gray-400"
                              title={lockedReason ?? t("services.lockedInvoiced")}
                            >
                              <Lock className="w-3.5 h-3.5" />
                            </span>
                          ) : canManage ? (
                            rowServiceId ? (
                              <Button
                                type="button"
                                variant="ghost"
                                disabled={apiBusy}
                                onClick={() => handleApiDelete(rowServiceId)}
                                className="text-red-600 hover:text-red-700 p-1"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            ) : null
                          ) : (
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => handleRemoveService(index)}
                              className="text-red-600 hover:text-red-700 p-1"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex justify-between items-center text-[12px]">
            <span className="font-semibold text-gray-600 uppercase tracking-wide">
              {t("services.totals")}
            </span>
            <div className="flex items-center gap-4">
              <div>
                <span className="text-gray-500">
                  {t("services.totalPrice")}
                </span>{" "}
                <span className="font-semibold text-gray-900">
                  € {grandTotal}
                </span>
              </div>
              {hasAnyTax && (
                <>
                  <div>
                    <span className="text-gray-500">
                      {t("services.taxTotal")}
                    </span>{" "}
                    <span className="font-semibold text-gray-900">
                      € {grandTax.toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">
                      {t("services.totalWithTax")}
                    </span>{" "}
                    <span className="font-semibold text-gray-900">
                      € {(grandTotal + grandTax).toFixed(2)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Post-completion: inline API-backed "add service" row */}
        {canManage && showApiAdd && (
          <ServiceAddInline
            services={visibleServices}
            busy={apiBusy}
            onSave={handleApiSave}
            onCancel={() => setShowApiAdd(false)}
          />
        )}
        </>
      )}

    </SectionCard>
  );
}
